import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Wrench, FloppyDisk, Trash, Plus, ChartLine, ArrowUp, ArrowDown, FilePdf, Upload } from "@phosphor-icons/react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import SectionHeader from "@/components/SectionHeader";

const MONTHS = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];

export default function OperationsFrontPlan({ canWrite }) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [dash, setDash] = useState(null);
  // Iter117: mini-form con año/mes por-fila para que sea explícito.
  // Defaults del header pero editables sin cambiar el selector global.
  const [formYear, setFormYear] = useState(now.getFullYear());
  const [formMonth, setFormMonth] = useState(now.getMonth() + 1);
  const [newFront, setNewFront] = useState("");
  const [newMeters, setNewMeters] = useState("");
  const [newActual, setNewActual] = useState("");
  const [busy, setBusy] = useState(false);
  const [rangeFrom, setRangeFrom] = useState(() => new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10));
  const [rangeTo, setRangeTo] = useState(() => now.toISOString().slice(0, 10));
  const [rangeBusy, setRangeBusy] = useState(false);
  const [importReport, setImportReport] = useState(null);
  const importRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/operations/front-plans/dashboard?year=${year}&month=${month}`);
      setDash(r.data);
    } catch (e) { toast.error(apiError(e)); setDash(null); }
  }, [year, month]);

  useEffect(() => { load(); }, [load]);

  const addOrUpdate = async () => {
    const name = newFront.trim();
    const meters = Number(newMeters || 0);
    const actual = newActual === "" ? null : Number(newActual);
    if (!name) { toast.warning("Escribe el nombre de la obra"); return; }
    if (meters <= 0) { toast.warning("Metros planeados > 0"); return; }
    setBusy(true);
    try {
      const payload = {
        year: Number(formYear), month: Number(formMonth),
        front_name: name, meters_planned: meters,
      };
      if (actual !== null && Number.isFinite(actual)) {
        payload.meters_actual_manual = actual;
      }
      await api.put("/operations/front-plans", payload);
      toast.success(`Plan ${name} guardado (${formMonth}/${formYear})`);
      setNewFront(""); setNewMeters(""); setNewActual("");
      // Si el usuario capturó para otro mes, brincamos el header allí
      if (Number(formYear) !== Number(year) || Number(formMonth) !== Number(month)) {
        setYear(Number(formYear)); setMonth(Number(formMonth));
      } else {
        load();
      }
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  // Iter117: ajuste manual de avance real de una obra sin abrir el mini-form.
  // El backend suma este valor al avance calculado desde operations_entries.
  const updateActualManual = async (row) => {
    const cur = row.meters_actual_manual || 0;
    const raw = window.prompt(
      `Ajuste manual de avance para «${row.front_name}»\n` +
      `Avance automático (capturas): ${row.real_ops ?? row.real} m\n` +
      `Ajuste manual actual: ${cur} m\n\n` +
      "Nuevo valor manual (m):",
      String(cur),
    );
    if (raw === null) return;
    const val = Number(raw);
    if (!Number.isFinite(val) || val < 0) { toast.warning("Valor inválido"); return; }
    try {
      await api.put("/operations/front-plans", {
        year: Number(year), month: Number(month),
        front_name: row.front_name,
        meters_planned: row.planned,
        meters_actual_manual: val,
      });
      toast.success(`Avance manual actualizado: ${val} m`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const downloadPeriodPdf = async () => {
    if (!rangeFrom || !rangeTo) { toast.warning("Selecciona rango de fechas"); return; }
    setRangeBusy(true);
    try {
      const r = await api.get(`/operations/front-plans/period-pdf?date_from=${rangeFrom}&date_to=${rangeTo}`,
        { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `plan-obra-${rangeFrom}_a_${rangeTo}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF descargado");
    } catch (e) { toast.error(apiError(e)); }
    finally { setRangeBusy(false); }
  };

  const importXlsx = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().match(/\.(xlsx|xlsm)$/)) {
      toast.error("Sube un archivo .xlsx o .xlsm"); return;
    }
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post("/operations/front-plans/import-xlsx", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const ok = (data.inserted || 0) + (data.updated || 0);
      const sk = data.skipped || 0;
      // Éxito parcial o completo — el toast va con la severidad correcta
      if (ok > 0 && sk === 0) {
        toast.success(`Importados: ${data.inserted} nuevos, ${data.updated} actualizados`);
      } else if (ok > 0 && sk > 0) {
        toast.warning(
          `Importados ${ok} pero ${sk} filas fueron ignoradas. Revisa el detalle abajo.`,
          { duration: 8000 },
        );
      } else {
        // 0 importados — mostramos el detalle directo al usuario
        toast.error(
          `${sk} filas ignoradas — 0 importadas. Revisa el detalle abajo.`,
          { duration: 10000 },
        );
      }
      // Guardamos el reporte crudo para render abajo del panel
      setImportReport(data);
      if (importRef.current) importRef.current.value = "";
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const updateInline = async (row, meters) => {
    try {
      await api.put("/operations/front-plans", { year: Number(year), month: Number(month), front_name: row.front_name, meters_planned: Number(meters || 0) });
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (planId) => {
    if (!window.confirm("¿Eliminar plan de esta obra?")) return;
    try { await api.delete(`/operations/front-plans/${planId}`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const yearOpts = [now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1, now.getFullYear() + 2];

  return (
    <div className="space-y-4" data-testid="ops-frontplan-panel">
      <SectionHeader icon={Wrench} title="Plan por Obra" eyebrow="Metros planeados · Avance diario acumulado" testid="ops-fp-header"
        right={
          <>
            <div>
              <Label className="overline-label">Año</Label>
              <select value={year} onChange={(e) => setYear(Number(e.target.value))}
                      data-testid="ops-fp-year" className="h-9 px-2 border border-slate-300 rounded text-sm">
                {yearOpts.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <div>
              <Label className="overline-label">Mes</Label>
              <select value={month} onChange={(e) => setMonth(Number(e.target.value))}
                      data-testid="ops-fp-month" className="h-9 px-2 border border-slate-300 rounded text-sm">
                {MONTHS.map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
              </select>
            </div>
          </>
        } />

      {canWrite && (
        <div className="bg-white border border-slate-200 rounded-lg p-3 flex flex-wrap items-end gap-2">
          <div className="w-24">
            <Label className="overline-label">Año</Label>
            <select value={formYear} onChange={(e) => setFormYear(Number(e.target.value))}
                    data-testid="ops-fp-form-year"
                    className="h-9 px-2 border border-slate-300 rounded text-sm w-full">
              {yearOpts.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div className="w-28">
            <Label className="overline-label">Mes</Label>
            <select value={formMonth} onChange={(e) => setFormMonth(Number(e.target.value))}
                    data-testid="ops-fp-form-month"
                    className="h-9 px-2 border border-slate-300 rounded text-sm w-full">
              {MONTHS.map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
            </select>
          </div>
          <div className="flex-1 min-w-[200px]">
            <Label className="overline-label">Obra / Frente</Label>
            <Input placeholder="Ej: Frente Norte 1, Rampa 3-A, Nivel -240" value={newFront}
                   onChange={(e) => setNewFront(e.target.value)} data-testid="ops-fp-new-name" />
          </div>
          <div className="w-32">
            <Label className="overline-label">Plan (m)</Label>
            <Input type="number" step="0.1" min={0} placeholder="0" value={newMeters}
                   onChange={(e) => setNewMeters(e.target.value)} data-testid="ops-fp-new-meters" />
          </div>
          <div className="w-32">
            <Label className="overline-label" title="Ajuste manual (opcional). Se suma al avance auto de las capturas.">
              Avance actual (m) <span className="text-slate-400">·opc</span>
            </Label>
            <Input type="number" step="0.1" min={0} placeholder="0" value={newActual}
                   onChange={(e) => setNewActual(e.target.value)} data-testid="ops-fp-new-actual" />
          </div>
          <Button onClick={addOrUpdate} disabled={busy || !newFront.trim() || !newMeters}
                  data-testid="ops-fp-add" className="bg-blue-900 hover:bg-blue-800 gap-1">
            <Plus size={13} weight="bold" /> Agregar / Actualizar
          </Button>
          <div className="border-l border-slate-200 h-10 mx-1" />
          <div>
            <Label className="overline-label">Importar Excel</Label>
            <Input ref={importRef} type="file" accept=".xlsx,.xlsm" disabled={busy}
              onChange={(e) => importXlsx(e.target.files?.[0])}
              data-testid="ops-fp-import-file"
              className="w-56 file:bg-amber-100 file:text-amber-800 file:border-0 file:mr-2 file:px-2 file:py-1 file:rounded" />
          </div>
          <Button variant="outline" onClick={async () => {
            try {
              const r = await api.get("/operations/front-plans/template-xlsx", { responseType: "blob" });
              const url = URL.createObjectURL(r.data);
              const a = document.createElement("a");
              a.href = url; a.download = "plantilla-plan-por-obra.xlsx"; a.click();
              URL.revokeObjectURL(url);
              toast.success("Plantilla descargada");
            } catch (e) { toast.error(apiError(e)); }
          }} data-testid="ops-fp-template" className="gap-1 h-9 border-amber-300 text-amber-800 hover:bg-amber-50">
            <Upload size={13} weight="bold" className="rotate-180" /> Plantilla
          </Button>
        </div>
      )}

      {/* Diagnóstico de importación — se llena tras subir un Excel. */}
      {importReport && (
        <div className={`border-2 rounded-lg p-3 space-y-2 ${(importReport.inserted + importReport.updated) > 0
                          ? "border-emerald-300 bg-emerald-50/60"
                          : "border-red-300 bg-red-50/60"}`}
             data-testid="ops-fp-import-report">
          <div className="flex items-center justify-between">
            <p className="font-heading font-black text-sm">
              Reporte de importación
              <span className="ml-2 text-[11px] font-normal text-slate-600">
                fila de encabezado: <b>#{importReport.header_row}</b> · headers detectados: <b>{(importReport.detected_headers || []).join(" · ") || "(ninguno)"}</b>
              </span>
            </p>
            <button onClick={() => setImportReport(null)}
                     className="text-xs text-slate-500 hover:text-slate-800"
                     data-testid="ops-fp-import-report-close">✕ cerrar</button>
          </div>
          <div className="flex gap-3 text-xs">
            <span className="px-2 py-1 rounded bg-emerald-100 text-emerald-800 font-bold">
              ✓ {importReport.inserted} nuevos
            </span>
            <span className="px-2 py-1 rounded bg-blue-100 text-blue-800 font-bold">
              ↻ {importReport.updated} actualizados
            </span>
            <span className={`px-2 py-1 rounded font-bold ${importReport.skipped > 0
                             ? "bg-red-100 text-red-800" : "bg-slate-100 text-slate-600"}`}>
              ⚠ {importReport.skipped} ignorados
            </span>
          </div>
          {importReport.skipped_rows && importReport.skipped_rows.length > 0 && (
            <div className="bg-white border border-red-200 rounded p-2 max-h-56 overflow-auto"
                 data-testid="ops-fp-import-skipped">
              <p className="text-[11px] font-bold text-red-700 mb-1">
                Filas ignoradas — razón exacta (primeras {importReport.skipped_rows.length}):
              </p>
              <table className="w-full text-[11px]">
                <thead>
                  <tr className="text-slate-500 border-b border-slate-200">
                    <th className="text-left py-1 pr-2 w-16">Fila</th>
                    <th className="text-left py-1">Motivo</th>
                  </tr>
                </thead>
                <tbody>
                  {importReport.skipped_rows.map((sr, i) => (
                    <tr key={i} className="border-b border-slate-100">
                      <td className="py-1 pr-2 font-mono font-bold">#{sr.row}</td>
                      <td className="py-1 text-red-700">{sr.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="text-[10px] text-slate-500 mt-1 italic">
                Tip: si los encabezados detectados no son los correctos, revisa la fila de headers de tu Excel o descarga la Plantilla oficial y copia tus datos allí.
              </p>
            </div>
          )}
          {importReport.inserted + importReport.updated === 0 && importReport.skipped === 0 && (
            <p className="text-xs text-amber-700 italic">El Excel no contenía filas con datos bajo la fila de encabezados.</p>
          )}
        </div>
      )}

      <div className="bg-white border border-blue-200 rounded-lg p-3 flex flex-wrap items-end gap-2 bg-blue-50/40">
        <div className="h-8 w-8 rounded bg-blue-100 flex items-center justify-center">
          <FilePdf size={16} weight="duotone" className="text-blue-700" />
        </div>
        <div>
          <Label className="overline-label text-blue-900">Reporte por periodo (todas las obras)</Label>
          <p className="text-[10px] text-slate-500">Suma todas las obras en el rango. Ideal para reunión de cierre semanal / mensual.</p>
        </div>
        <div className="flex-1" />
        <div>
          <Label className="overline-label text-[10px]">Desde</Label>
          <Input type="date" value={rangeFrom} onChange={(e) => setRangeFrom(e.target.value)}
                 data-testid="ops-fp-range-from" className="h-8 w-36 text-xs" />
        </div>
        <div>
          <Label className="overline-label text-[10px]">Hasta</Label>
          <Input type="date" value={rangeTo} onChange={(e) => setRangeTo(e.target.value)}
                 data-testid="ops-fp-range-to" className="h-8 w-36 text-xs" />
        </div>
        <Button onClick={downloadPeriodPdf} disabled={rangeBusy || !rangeFrom || !rangeTo}
                data-testid="ops-fp-range-pdf" className="bg-blue-700 hover:bg-blue-800 gap-1 h-8">
          <FilePdf size={13} weight="bold" /> {rangeBusy ? "Generando…" : "Descargar PDF"}
        </Button>
      </div>

      {!dash || dash.fronts.length === 0 ? (
        <p className="text-sm text-slate-400 text-center py-10 italic">
          Sin planes ni avance para {MONTHS[month - 1]} {year}. Agrega una obra arriba, o registra entries en Operaciones para ver avance real.
        </p>
      ) : (
        <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
          <table className="w-full text-xs" data-testid="ops-fp-table">
            <thead className="bg-slate-900 text-white">
              <tr>
                <th className="text-left px-3 py-2">Obra / Frente</th>
                <th className="text-right px-3 py-2">Planeado (m)</th>
                <th className="text-right px-3 py-2">Real (m)</th>
                <th className="text-right px-3 py-2">Δ%</th>
                <th className="text-center px-3 py-2 min-w-[300px]">
                  <ChartLine size={13} weight="bold" className="inline mr-1" />
                  Avance diario acumulado
                </th>
                {canWrite && <th className="text-center px-3 py-2 w-14"></th>}
              </tr>
            </thead>
            <tbody>
              {dash.fronts.map((f) => {
                const pct = f.planned > 0 ? Math.min(100, Math.round(f.real / f.planned * 100)) : 0;
                const v = f.variance_pct;
                const maxCum = Math.max(f.planned, ...(f.daily.map((d) => d.cumulative) || [1])) || 1;
                return (
                  <tr key={f.front_name} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`ops-fp-row-${f.front_name}`}>
                    <td className="px-3 py-2 font-semibold text-slate-800">{f.front_name}</td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {f.plan_id ? (
                        <Input type="number" step="0.1" min={0} defaultValue={f.planned} disabled={!canWrite}
                               onBlur={(e) => Number(e.target.value) !== f.planned && updateInline(f, e.target.value)}
                               className="h-7 w-24 text-right text-xs tabular-nums ml-auto"
                               data-testid={`ops-fp-planned-${f.front_name}`} />
                      ) : <span className="text-slate-400 italic">—</span>}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      <div className="flex items-center justify-end gap-1">
                        <span className="font-bold">{f.real.toLocaleString()}</span>
                        {canWrite && f.plan_id && (
                          <button type="button" onClick={() => updateActualManual(f)}
                                  data-testid={`ops-fp-edit-actual-${f.front_name}`}
                                  className="text-[10px] px-1.5 py-0.5 rounded border border-amber-300 text-amber-800 bg-amber-50 hover:bg-amber-100"
                                  title={`Auto (capturas): ${f.real_ops ?? f.real} m · Manual: ${f.meters_actual_manual || 0} m`}>
                            ✎ manual
                          </button>
                        )}
                      </div>
                      {(f.meters_actual_manual || 0) !== 0 && (
                        <div className="text-[9px] text-slate-500 italic">
                          auto {(f.real_ops ?? 0).toLocaleString()} + manual {(f.meters_actual_manual || 0).toLocaleString()}
                        </div>
                      )}
                    </td>
                    <td className={`px-3 py-2 text-right tabular-nums font-bold ${v === null ? "text-slate-300" : v >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                      {v === null ? "—" : (
                        <span className="inline-flex items-center gap-0.5">
                          {v >= 0 ? <ArrowUp size={9} weight="bold" /> : <ArrowDown size={9} weight="bold" />}
                          {Math.abs(v)}%
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1">
                        {/* barra progreso */}
                        <div className="flex-1 h-2 bg-slate-200 rounded overflow-hidden">
                          <div className={`h-full ${pct >= 100 ? "bg-emerald-600" : pct >= 70 ? "bg-blue-600" : "bg-amber-500"}`} style={{ width: `${pct}%` }} />
                        </div>
                        <span className="text-[10px] font-bold text-slate-600 w-8 text-right">{pct}%</span>
                      </div>
                      {/* sparkline por día */}
                      {f.daily.length > 0 && (
                        <svg viewBox={`0 0 ${Math.max(f.daily.length * 8, 40)} 20`} className="w-full h-6 mt-1" preserveAspectRatio="none">
                          <polyline
                            fill="none"
                            stroke="#3B82F6"
                            strokeWidth="1.5"
                            points={f.daily.map((d, i) => `${i * 8},${20 - (d.cumulative / maxCum) * 18}`).join(" ")}
                          />
                          {f.planned > 0 && (
                            <line x1="0" x2={f.daily.length * 8} y1={20 - (f.planned / maxCum) * 18} y2={20 - (f.planned / maxCum) * 18}
                                  stroke="#DC2626" strokeWidth="0.6" strokeDasharray="2 2" />
                          )}
                        </svg>
                      )}
                    </td>
                    {canWrite && (
                      <td className="px-3 py-2 text-center">
                        {f.plan_id && (
                          <button onClick={() => remove(f.plan_id)} data-testid={`ops-fp-del-${f.front_name}`} className="text-red-500 hover:text-red-700">
                            <Trash size={13} weight="bold" />
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
              <tr className="bg-slate-100 font-bold border-t-2 border-slate-400">
                <td className="px-3 py-2 text-slate-900">Total {MONTHS[month - 1]}</td>
                <td className="px-3 py-2 text-right tabular-nums">{dash.totals.planned.toLocaleString()}</td>
                <td className="px-3 py-2 text-right tabular-nums">{dash.totals.real.toLocaleString()}</td>
                <td className={`px-3 py-2 text-right tabular-nums ${dash.totals.variance_pct === null ? "text-slate-400" : dash.totals.variance_pct >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                  {dash.totals.variance_pct === null ? "—" : `${dash.totals.variance_pct >= 0 ? "+" : ""}${dash.totals.variance_pct}%`}
                </td>
                <td colSpan={canWrite ? 2 : 1}></td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
      <p className="text-[10px] text-slate-500 italic">
        <b>Real</b> = suma de metros por obra (campo <b>área</b>, sin distinguir mayúsculas) desde capturas de Mi Turno / Operaciones: <b>Voladuras</b> (avance m), <b>Jumbo</b> (metros) y <b>Anclador</b> (metros instalados). Línea roja punteada del sparkline = meta planeada.
      </p>
    </div>
  );
}
